import {
  Room,
  RoomEvent,
  Track,
  type RemoteParticipant,
} from 'livekit-client';

/** Play remote microphone audio in the browser (subscriptions alone do not emit sound). */
export function attachRemoteAudioPlayback(room: Room): () => void {
  const attachedAudioElsByTrack = new WeakMap<Track, HTMLAudioElement>();
  const attachedAudioEls = new Set<HTMLAudioElement>();

  const attachAudioTrack = (track: Track): void => {
    if (track.kind !== Track.Kind.Audio) return;
    if (attachedAudioElsByTrack.has(track)) return;
    const el = track.attach() as HTMLAudioElement;
    el.autoplay = true;
    el.style.display = 'none';
    document.body.appendChild(el);
    attachedAudioElsByTrack.set(track, el);
    attachedAudioEls.add(el);
  };

  const detachAudioTrack = (track: Track): void => {
    if (track.kind !== Track.Kind.Audio) return;
    const el = attachedAudioElsByTrack.get(track);
    if (el) {
      track.detach(el);
      el.remove();
      attachedAudioEls.delete(el);
      return;
    }
    track.detach();
  };

  const attachParticipantAudioTracks = (participant: RemoteParticipant): void => {
    participant.trackPublications.forEach((publication) => {
      const track = publication.track;
      if (track && track.kind === Track.Kind.Audio) {
        attachAudioTrack(track);
      }
    });
  };

  const onConnected = (): void => {
    room.remoteParticipants.forEach((participant) => attachParticipantAudioTracks(participant));
  };
  const onParticipantConnected = (participant: RemoteParticipant): void => {
    attachParticipantAudioTracks(participant);
  };
  const onTrackSubscribed = (track: Track): void => {
    attachAudioTrack(track);
  };
  const onTrackUnsubscribed = (track: Track): void => {
    detachAudioTrack(track);
  };

  room.on(RoomEvent.Connected, onConnected);
  room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
  room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
  room.on(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);

  return () => {
    room.off(RoomEvent.Connected, onConnected);
    room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
    room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.off(RoomEvent.TrackUnsubscribed, onTrackUnsubscribed);
    attachedAudioEls.forEach((el) => {
      try {
        el.srcObject = null;
        el.src = '';
      } catch {
        // Best-effort cleanup.
      }
      el.remove();
    });
    attachedAudioEls.clear();
  };
}
